// App-wide date formatting helpers. Every user-facing date renders
// as MM-DD-YYYY; date+time renders as MM-DD-YYYY HH:MM AM/PM.
//
// `<input type="date">` values are intentionally left alone — the
// browser's date picker controls that surface and forcing a custom
// format breaks native validation.
//
// Server-side rendering (PDFs via docgen, audit JSON dumps) is also
// out of scope here; if a partner asks for the same format in
// generated documents, that's a separate pass on the docgen layer.

type DateInput = Date | string | number | null | undefined;

function toDate(input: DateInput): Date | null {
  if (input == null || input === "") return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  // YYYY-MM-DD strings parsed as local-midnight rather than UTC so
  // the displayed month/day doesn't drift across timezones (a
  // YYYY-MM-DD value coming from a `date` Postgres column is a
  // calendar day, not a UTC instant).
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split("-").map(Number);
    return new Date(y!, (m ?? 1) - 1, d ?? 1);
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatDate(input: DateInput, fallback = "—"): string {
  const d = toDate(input);
  if (!d) return fallback;
  return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`;
}

export function formatDateTime(input: DateInput, fallback = "—"): string {
  const d = toDate(input);
  if (!d) return fallback;
  let hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${formatDate(d)} ${hours}:${pad2(d.getMinutes())} ${ampm}`;
}
